import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Lock, LockOpen, Plus, Printer, RefreshCw, Trash2, Wallet } from 'lucide-react';
import type { PayrollLineDto, PayrollRunDto, PeriodDto } from '@workforce/shared';
import { api } from '@/lib/api';
import { periodName, t } from '@/lib/i18n';
import { useTz } from '@/lib/timezone';
import { money } from '@/components/SalaryTab';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, Notice, PageHeader, Select, Spinner, StatCard } from '@/components/ui';
import { ExportMenu } from '@/components/ExportMenu';
import { periodRange } from './Reports';

type RunResponse = PayrollRunDto & { neededRates: (PayrollRunDto['neededRates'][number] & { lastRate?: number | null })[] };
type PayrollPeriod = PeriodDto & { payrollStatus: 'draft' | 'finalized' };

const hrs = (n: number) => t('{n} h', { n: n.toLocaleString('en-US', { maximumFractionDigits: 2 }) });

export function PayrollPage() {
  const qc = useQueryClient();
  const { fmt } = useTz();
  const [params, setParams] = useSearchParams();
  const periods = useQuery({ queryKey: ['payroll', 'periods'], queryFn: () => api.get<PayrollPeriod[]>('/payroll/periods') });
  const fallback = useMemo(() => {
    const list = periods.data ?? [];
    return (list.find((p) => p.hasEnded && p.payrollStatus === 'draft') ?? list.find((p) => p.hasEnded) ?? list[0])?.id;
  }, [periods.data]);
  const periodId = params.get('period') ?? fallback;
  useEffect(() => {
    if (!params.get('period') && fallback) setParams({ period: fallback }, { replace: true });
  }, [fallback, params, setParams]);

  const q = useQuery({
    queryKey: ['payroll', 'run', periodId],
    queryFn: () => api.get<RunResponse>(`/payroll/runs/${periodId}`),
    enabled: !!periodId,
  });
  const setData = (d: PayrollRunDto) => {
    qc.setQueryData(['payroll', 'run', periodId], (old: RunResponse | undefined) => ({ ...d, neededRates: old?.neededRates ?? d.neededRates }));
    qc.invalidateQueries({ queryKey: ['payroll', 'run', periodId] });
    qc.invalidateQueries({ queryKey: ['payroll', 'periods'] });
  };
  const [dialog, setDialog] = useState<'finalize' | 'reopen' | null>(null);
  const finalize = useMutation({ mutationFn: () => api.post<PayrollRunDto>(`/payroll/runs/${periodId}/finalize`, {}), onSuccess: (d) => { setDialog(null); setData(d); } });
  const reopen = useMutation({ mutationFn: (reason: string) => api.post<PayrollRunDto>(`/payroll/runs/${periodId}/reopen`, { reason }), onSuccess: (d) => { setDialog(null); setData(d); } });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [adjFor, setAdjFor] = useState<PayrollLineDto | null>(null);

  if (periods.isLoading) return <Spinner />;
  const r = q.data;
  const draft = r?.run.status !== 'finalized';

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('Payroll')}
        subtitle={t('Monthly salaries from the hours worked in each reporting period: full salary for the required hours, overtime above them.')}
        actions={
          <div className="flex gap-2">
            {periodId && <ExportMenu kind="payroll" params={{ period: periodId }} extra={[
                  { label: t('Payslips (PDF, one page each)'), href: `/api/exports/payslips?period=${periodId}` },
                  { label: t('Bank transfer list (Excel)'), href: `/api/exports/bank-transfers?period=${periodId}&format=xlsx` },
                ]} />}
            <Button variant="secondary" icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>
              {t('Print')}
            </Button>
          </div>
        }
      />
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <Field label={t('Month (reporting period)')} className="min-w-72">
          <Select value={periodId ?? ''} onChange={(e) => setParams({ period: e.target.value })}>
            {periods.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {periodName(p.label)} · {periodRange(p)} · {p.payrollStatus === 'finalized' ? t('paid (final)') : p.hasEnded ? t('to prepare') : t('running')}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <ErrorBanner error={q.error} />
      {q.isLoading && <Spinner />}
      {r && (
        <>
          <Card>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="rounded-lg bg-emerald-50 p-2 text-emerald-700"><Wallet className="h-5 w-5" /></span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold text-slate-900">{periodName(r.period.label)}</h2>
                    {r.run.status === 'finalized' ? <Badge tone="green">{t('Final')}</Badge> : <Badge tone="amber">{t('Draft')}</Badge>}
                    {r.period.status === 'closed' ? <Badge>{t('Hours frozen')}</Badge> : <Badge tone="slate">{t('Hours still open')}</Badge>}
                  </div>
                  <p className="text-sm text-slate-500">{periodRange(r.period)}</p>
                  {r.run.status === 'finalized' && (
                    <p className="mt-1 text-xs text-slate-500">
                      {t('Finalised by {name} on {date}', { name: r.run.finalizedBy ?? '—', date: fmt(r.run.finalizedAt, null, 'dd LLL yyyy, HH:mm') })}
                      {r.run.version > 1 ? ` · ${t('version {n}', { n: r.run.version })}` : ''}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 print:hidden">
                {draft ? (
                  <Button icon={<Lock className="h-4 w-4" />} disabled={r.blockers.length > 0} onClick={() => setDialog('finalize')}>
                    {t('Finalise payroll')}
                  </Button>
                ) : (
                  <Button variant="secondary" icon={<LockOpen className="h-4 w-4" />} onClick={() => setDialog('reopen')}>
                    {t('Reopen')}
                  </Button>
                )}
              </div>
            </div>
            {draft && r.blockers.length > 0 && (
              <div className="mt-4 print:hidden">
                <Notice tone="amber">
                  <p className="mb-1 font-medium">{t('Before finalising')}</p>
                  <ul className="list-disc ps-5">
                    {r.blockers.map((b) => (
                      <li key={b}>
                        {t(b)} {b.startsWith('Close the reporting period') && <Link to={`/reports?period=${r.period.id}`} className="font-medium underline">{t('Open Reports →')}</Link>}
                      </li>
                    ))}
                  </ul>
                </Notice>
              </div>
            )}
          </Card>

          <RatesCard r={r} periodId={periodId!} onData={setData} />

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {r.totals.map((tot) => (
              <StatCard key={tot.currency} label={t('Total to pay ({currency})', { currency: tot.currency })} value={money(tot.netPay, tot.currency)} hint={t(tot.employees === 1 ? '{n} employee' : '{n} employees', { n: tot.employees })} tone="green" />
            ))}
            <StatCard
              label={t('Rules')}
              value={<span className="text-sm font-medium">{t('Overtime × {n}', { n: r.rules.overtimeMultiplier })}</span>}
              hint={
                <>
                  {t('Hourly rate = salary ÷ {hours}', { hours: r.rules.hourlyRateHours > 0 ? t('{n} h', { n: r.rules.hourlyRateHours }) : t("the month's required hours") })} ·{' '}
                  {r.rules.shortfall === 'percentage' ? t('paid by percentage of hours completed') : r.rules.shortfall === 'deduct' ? t('missing hours deducted') : t('missing hours not deducted')} ·{' '}
                  <Link to="/settings" className="underline print:hidden">{t('change')}</Link>
                </>
              }
            />
          </div>

          <Card bodyClass="p-0" title={r.source === 'finalized' ? t('Payslips (final)') : t('Payslips (draft — recalculated live)')}>
            {r.lines.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
                      <th className="w-8 px-2 py-2" />
                      <th className="px-3 py-2">{t('Employee')}</th>
                      <th className="hidden px-3 py-2 lg:table-cell">{t('Salary')}</th>
                      <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Base')}</th>
                      <th className="hidden px-3 py-2 text-end md:table-cell">{t('Required')}</th>
                      <th className="hidden px-3 py-2 text-end md:table-cell">{t('Paid hours')}</th>
                      <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Overtime')}</th>
                      <th className="hidden px-3 py-2 text-end md:table-cell">{t('Short')}</th>
                      <th className="hidden px-3 py-2 text-end lg:table-cell">{t('Adjust.')}</th>
                      <th className="px-3 py-2 text-end">{t('Net pay')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.lines.map((l) => (
                      <Fragment key={l.employeeId}>
                        <tr className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => setOpen((o) => ({ ...o, [l.employeeId]: !o[l.employeeId] }))}>
                          <td className="px-2 py-2 text-slate-400">{open[l.employeeId] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                          <td className="whitespace-nowrap px-3 py-2">
                            <span className="font-medium">{l.employeeName}</span>
                            {l.notes.length > 0 && <span className="ms-1.5 text-amber-600" title={l.notes.map((n) => t(n)).join('\n')}>•</span>}
                            <p className="text-xs text-slate-400">{l.employeeCode}</p>
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-xs text-slate-600 lg:table-cell">
                            {money(l.salaryAmount, l.salaryCurrency)}
                            {l.rateSource === 'fixed' && <Badge tone="violet">{t('fixed')}</Badge>}
                            {l.rateSource === 'period' && <span className="text-slate-400"> × {l.rate}</span>}
                            {l.rateSource === 'missing' && <span className="text-amber-700"> · {t('rate?')}</span>}
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums sm:table-cell">{l.rateSource === 'missing' ? '—' : money(l.baseSalary)}</td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums text-slate-500 md:table-cell">{hrs(l.requiredHours)}</td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums md:table-cell">{l.timeTracked ? hrs(l.paidHours) : '—'}</td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums sm:table-cell">
                            {l.overtimePay ? <span className="text-emerald-700">+{money(l.overtimePay)}</span> : '—'}
                            {l.overtimeHours > 0 && <p className="text-xs text-slate-400">{hrs(l.overtimeHours)}</p>}
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums md:table-cell">
                            {l.deduction ? <span className="text-red-600">−{money(l.deduction)}</span> : l.shortHours > 0 ? hrs(l.shortHours) : '—'}
                            {l.deduction > 0 && <p className="text-xs text-slate-400">{hrs(l.shortHours)}</p>}
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums lg:table-cell">
                            {l.adjustmentsTotal ? <span className={l.adjustmentsTotal > 0 ? 'text-emerald-700' : 'text-red-600'}>{l.adjustmentsTotal > 0 ? '+' : ''}{money(l.adjustmentsTotal)}</span> : '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-end font-semibold tabular-nums">{l.rateSource === 'missing' ? '—' : money(l.netPay, l.payCurrency)}</td>
                        </tr>
                        {open[l.employeeId] && (
                          <tr className="border-b border-slate-100 bg-slate-50/60">
                            <td />
                            <td colSpan={9} className="px-3 py-3">
                              <Breakdown l={l} r={r} onAdjust={draft ? () => setAdjFor(l) : undefined} onData={setData} periodId={periodId!} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title={t('No salaries yet')} description={t("Set each engineer's salary on their employee page (Salary tab).")} />
            )}
          </Card>

          {r.withoutSalary.length > 0 && (
            <Card title={t('Without a salary')}>
              <p className="mb-2 text-sm text-slate-500">{t('These people worked in this period but have no salary set, so they are not in the payroll.')}</p>
              <div className="flex flex-wrap gap-2">
                {r.withoutSalary.map((w) => (
                  <Link key={w.employeeId} to={`/employees/${w.employeeId}?tab=salary`} className="rounded-full border border-slate-200 px-3 py-1 text-sm text-brand-700 hover:bg-slate-50">
                    {w.employeeName} {t('→')}
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {adjFor && r && (
        <AdjustmentModal line={adjFor} periodId={periodId!} onClose={() => setAdjFor(null)} onData={(d) => { setAdjFor(null); setData(d); }} />
      )}
      <ConfirmDialog
        open={dialog === 'finalize'}
        onClose={() => setDialog(null)}
        onConfirm={() => finalize.mutate()}
        title={t('Finalise payroll for {period}?', { period: r ? periodName(r.period.label) : '' })}
        message={t('The payslips are frozen as shown. The rate, adjustments and the hours of this period can no longer change unless an administrator reopens the payroll with a reason.')}
        confirmLabel={t('Finalise')}
        loading={finalize.isPending}
        error={finalize.error}
      />
      <ConfirmDialog
        open={dialog === 'reopen'}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => reopen.mutate(reason)}
        title={t('Reopen payroll?')}
        message={t('The final payslips are kept as an earlier version. You can then change the rate or adjustments and finalise again.')}
        confirmLabel={t('Reopen')}
        danger
        requireReason
        loading={reopen.isPending}
        error={reopen.error}
      />
    </div>
  );
}

function RatesCard({ r, periodId, onData }: { r: RunResponse; periodId: string; onData: (d: PayrollRunDto) => void }) {
  const { fmt } = useTz();
  const pairs = useMemo(() => {
    const map = new Map<string, { fromCurrency: string; toCurrency: string; lastRate?: number | null }>();
    for (const n of r.neededRates) map.set(`${n.fromCurrency}>${n.toCurrency}`, n);
    for (const x of r.rates) if (!map.has(`${x.fromCurrency}>${x.toCurrency}`)) map.set(`${x.fromCurrency}>${x.toCurrency}`, x);
    return [...map.values()];
  }, [r]);
  if (!pairs.length) return null;
  return (
    <Card title={t('Exchange rate for this month')}>
      <div className="space-y-4">
        {pairs.map((p) => (
          <RateRow key={`${p.fromCurrency}${p.toCurrency}`} pair={p} r={r} periodId={periodId} onData={onData} fmt={fmt} />
        ))}
        <p className="text-xs text-slate-500">{t('Used for salaries without a fixed rate. Set it at the end of the month; engineers with a fixed rate are not affected.')}</p>
      </div>
    </Card>
  );
}

function RateRow({
  pair,
  r,
  periodId,
  onData,
  fmt,
}: {
  pair: { fromCurrency: string; toCurrency: string; lastRate?: number | null };
  r: RunResponse;
  periodId: string;
  onData: (d: PayrollRunDto) => void;
  fmt: ReturnType<typeof useTz>['fmt'];
}) {
  const current = r.rates.find((x) => x.fromCurrency === pair.fromCurrency && x.toCurrency === pair.toCurrency);
  const [value, setValue] = useState(current ? String(current.rate) : '');
  useEffect(() => setValue(current ? String(current.rate) : ''), [current?.rate]); // eslint-disable-line react-hooks/exhaustive-deps
  const draft = r.run.status !== 'finalized';
  const save = useMutation({
    mutationFn: () => api.put<PayrollRunDto>(`/payroll/runs/${periodId}/rates`, { fromCurrency: pair.fromCurrency, toCurrency: pair.toCurrency, rate: Number(value) }),
    onSuccess: onData,
  });
  const online = useMutation({
    mutationFn: () => api.post<PayrollRunDto>(`/payroll/runs/${periodId}/rates/fetch`, { fromCurrency: pair.fromCurrency, toCurrency: pair.toCurrency }),
    onSuccess: onData,
  });
  const changed = value !== '' && Number(value) > 0 && Number(value) !== current?.rate;
  return (
    <div>
      <ErrorBanner error={save.error ?? online.error} />
      <div className="flex flex-wrap items-end gap-2">
        <Field label={`1 ${pair.fromCurrency} =`}>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.0001" min="0" className="w-36" value={value} disabled={!draft} onChange={(e) => setValue(e.target.value)} aria-label={t('{from} to {to} rate', { from: pair.fromCurrency, to: pair.toCurrency })} />
            <span className="text-sm font-medium text-slate-600">{pair.toCurrency}</span>
          </div>
        </Field>
        {draft && (
          <>
            <Button disabled={!changed} loading={save.isPending} onClick={() => save.mutate()}>
              {t('Save rate')}
            </Button>
            <Button variant="secondary" icon={<RefreshCw className={clsx('h-4 w-4', online.isPending && 'animate-spin')} />} disabled={online.isPending} onClick={() => online.mutate()}>
              {t("Get today's rate online")}
            </Button>
          </>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {current
          ? `${current.source === 'online' ? t('From {source}', { source: current.sourceNote ?? t('online source') }) : t('Entered manually')} · ${current.updatedBy ?? ''} ${fmt(current.updatedAt, null, 'dd LLL yyyy, HH:mm')}`
          : pair.lastRate
            ? t('Not set for this month yet · last month: {rate}', { rate: pair.lastRate })
            : t('Not set for this month yet.')}
      </p>
    </div>
  );
}

function Breakdown({ l, r, onAdjust, onData, periodId }: { l: PayrollLineDto; r: RunResponse; onAdjust?: () => void; onData: (d: PayrollRunDto) => void; periodId: string }) {
  const remove = useMutation({
    mutationFn: (id: string) => api.del<PayrollRunDto>(`/payroll/runs/${periodId}/adjustments/${id}`),
    onSuccess: onData,
  });
  const cur = l.payCurrency;
  const basis = r.rules.hourlyRateHours > 0 ? t('{n} h', { n: r.rules.hourlyRateHours }) : t('{n} h required', { n: l.requiredHours });
  return (
    <div className="grid gap-4 text-sm md:grid-cols-2">
      <div className="space-y-1 text-slate-700">
        <p>
          <span className="text-slate-500">{t('Salary:')}</span> {money(l.salaryAmount, l.salaryCurrency)}
          {l.rateSource === 'fixed' && ` ${t('× fixed rate {rate} = {amount}', { rate: l.rate?.toFixed(4), amount: money(l.baseSalary, cur) })}`}
          {l.rateSource === 'period' && ` ${t('× month rate {rate} = {amount}', { rate: l.rate, amount: money(l.baseSalary, cur) })}`}
          {l.employedShare < 1 && ` ${t('(prorated {pct}%)', { pct: Math.round(l.employedShare * 100) })}`}
        </p>
        {l.timeTracked ? (
          <>
            <p>
              <span className="text-slate-500">{t('Hours:')}</span> {t('required {required} · worked {worked}', { required: hrs(l.requiredHours), worked: hrs(l.workedHours) })}
              {l.paidLeaveHours > 0 && ` ${t('+ paid leave {hours}', { hours: hrs(l.paidLeaveHours) })}`} = <b>{hrs(l.paidHours)}</b>
            </p>
            <p>
              <span className="text-slate-500">{t('Hourly rate:')}</span> {money(l.rateSource === 'missing' ? null : Math.round(l.hourlyRate * 100) / 100, cur, 2)} ({money(Math.round(l.hourlyRate * (r.rules.hourlyRateHours || l.requiredHours)), cur)} ÷ {basis})
            </p>
            {l.overtimeHours > 0 && (
              <p>
                <span className="text-slate-500">{t('Overtime:')}</span> {hrs(l.overtimeHours)} × {money(Math.round(l.hourlyRate * 100) / 100, undefined, 2)} × {l.overtimeMultiplier} = <b className="text-emerald-700">+{money(l.overtimePay, cur)}</b>
              </p>
            )}
            {l.shortHours > 0 && (
              <p>
                <span className="text-slate-500">{t('Missing hours:')}</span> {hrs(l.shortHours)}
                {l.deduction > 0 ? <> × {money(Math.round(l.hourlyRate * 100) / 100, undefined, 2)} = <b className="text-red-600">−{money(l.deduction, cur)}</b></> : ` ${t('(not deducted)')}`}
              </p>
            )}
            {!l.overtimeEligible && <p className="text-xs text-slate-500">{t('No overtime on this salary.')}</p>}
          </>
        ) : (
          <p className="text-slate-500">{t('Does not clock in — fixed monthly salary.')}</p>
        )}
        <p className="pt-1 font-semibold">{t('Net pay: {amount}', { amount: money(l.netPay, cur) })}</p>
        {l.notes.map((n) => (
          <p key={n} className="text-xs text-amber-700">{t(n)}</p>
        ))}
        <Link to={`/attendance?tab=employee&employee=${l.employeeId}&from=${r.period.startDate}&to=${r.period.endDate}`} className="inline-block pt-1 text-xs font-medium text-brand-700 hover:underline print:hidden">
          {t('Day-by-day hours →')}
        </Link>
        <a href={`/api/exports/payslips?period=${r.period.id}&employee=${l.employeeId}`} download className="ms-4 inline-block pt-1 text-xs font-medium text-brand-700 hover:underline print:hidden">
          {t('Payslip (PDF)')}
        </a>
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('Adjustments')}</p>
          {onAdjust && (
            <Button size="sm" variant="ghost" icon={<Plus className="h-3.5 w-3.5" />} onClick={onAdjust}>
              {t('Add')}
            </Button>
          )}
        </div>
        {l.adjustments.length ? (
          <ul className="space-y-1">
            {l.adjustments.map((a) => (
              <li key={a.id} className="flex items-center gap-2">
                <span className={clsx('w-24 text-end tabular-nums', a.amount > 0 ? 'text-emerald-700' : 'text-red-600')}>
                  {a.amount > 0 ? '+' : ''}
                  {money(a.amount)}
                </span>
                <span className="flex-1 text-slate-600">{a.note}</span>
                <span className="text-xs text-slate-400">{a.createdBy}</span>
                {onAdjust && (
                  <button className="text-slate-400 hover:text-red-600 print:hidden" onClick={() => remove.mutate(a.id)} aria-label={t('Remove adjustment')}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-slate-500">{t('None — e.g. a bonus, allowance or advance to deduct.')}</p>
        )}
      </div>
    </div>
  );
}

function AdjustmentModal({ line, periodId, onClose, onData }: { line: PayrollLineDto; periodId: string; onClose: () => void; onData: (d: PayrollRunDto) => void }) {
  const [kind, setKind] = useState<'add' | 'deduct'>('add');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api.post<PayrollRunDto>(`/payroll/runs/${periodId}/adjustments`, { employeeId: line.employeeId, amount: (kind === 'add' ? 1 : -1) * Number(amount), note }),
    onSuccess: onData,
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={t('Adjustment — {name}', { name: line.employeeName })}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('Cancel')}</Button>
          <Button loading={m.isPending} disabled={!(Number(amount) > 0) || note.trim().length < 3} onClick={() => m.mutate()}>
            {t('Add')}
          </Button>
        </>
      }
    >
      <ErrorBanner error={m.error} />
      <div className="space-y-4">
        <Field label={t('Type')}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as 'add' | 'deduct')}>
            <option value="add">{t('Add (bonus, allowance…)')}</option>
            <option value="deduct">{t('Deduct (advance, penalty…)')}</option>
          </Select>
        </Field>
        <Field label={t('Amount ({currency})', { currency: line.payCurrency })}>
          <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label={t('Reason')} hint={t('Shown on the payslip and recorded in the audit log.')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder={t('e.g. Transport allowance')} />
        </Field>
      </div>
    </Modal>
  );
}

